import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { EasyCheckButton } from '../../common/Controls';
import { mobileBeneficiaryService } from '../../../services/mobile/mobileBeneficiaryService';

const MobileBeneficiaryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(isEditMode);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      beneficiaryName: '',
      isActive: true
    }
  });

  // Load existing beneficiary for edit mode
  useEffect(() => {
    if (!isEditMode) return;

    const fetchBeneficiary = async () => {
      setDataLoading(true);
      try {
        const result = await mobileBeneficiaryService.getBeneficiaryById(id);
        if (result.success && result.data) {
          const b = result.data;
          reset({
            beneficiaryName: b.BeneficiaryName || b.beneficiaryName || '',
            isActive: b.IsActive !== undefined ? b.IsActive : (b.isActive !== undefined ? b.isActive : true)
          });
        }
      } catch {
        setSnackbar({ open: true, message: 'Failed to load beneficiary.', severity: 'error' });
      } finally {
        setDataLoading(false);
      }
    };

    fetchBeneficiary();
  }, [id, isEditMode, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        beneficiaryName: data.beneficiaryName.trim(),
        isActive: data.isActive
      };

      const result = isEditMode
        ? await mobileBeneficiaryService.updateBeneficiary(id, payload)
        : await mobileBeneficiaryService.createBeneficiary(payload);

      if (result.success) {
        if (isEditMode) {
          navigate(`/mobile/beneficiaries/${id}`);
        } else {
          navigate('/mobile/beneficiaries');
        }
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to save beneficiary.', severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: 'An error occurred.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#2563eb' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Back Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton
          onClick={() => isEditMode ? navigate(`/mobile/beneficiaries/${id}`) : navigate('/mobile/beneficiaries')}
          sx={{ mr: 1 }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
          {isEditMode ? 'Edit Beneficiary' : 'New Beneficiary'}
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {/* Beneficiary Name */}
        <Controller
          name="beneficiaryName"
          control={control}
          rules={{
            required: 'Beneficiary name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' },
            maxLength: { value: 100, message: 'Name must not exceed 100 characters' }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Beneficiary Name"
              error={!!errors.beneficiaryName}
              helperText={errors.beneficiaryName?.message}
              autoFocus
              sx={{ mb: 2 }}
              InputProps={{ sx: { borderRadius: 2, height: 56 } }}
            />
          )}
        />

        {/* Is Active Toggle */}
        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 500 }}>
                  {field.value ? 'Active' : 'Inactive'}
                </Typography>
              }
              sx={{ mb: 3, ml: 0 }}
            />
          )}
        />

        <EasyCheckButton
          type="submit"
          fullWidth
          disabled={loading}
          sx={{
            py: 1.75,
            borderRadius: 2,
            backgroundColor: '#2563eb',
            '&:hover': { backgroundColor: '#1d4ed8' },
            fontWeight: 600,
            fontSize: '1rem'
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? 'Update Beneficiary' : 'Add Beneficiary')}
        </EasyCheckButton>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MobileBeneficiaryForm;
